/**
 * Draggable Mixin Factory
 *
 * Creates a Vue 2 mixin that provides drag functionality for any element.
 * Supports both regular Vue elements (reactive style binding) and Vuetify v-dialog
 * components (direct DOM manipulation for portal workaround).
 *
 * @module mixins/draggableMixin/createDraggableMixin
 * @version 1.0.1
 * @see .claude/features/DRAGGABLE_MIXIN.md for full documentation
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * QUICK START (3 Steps)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Step 1: Import and configure the mixin
 * ─────────────────────────────────────────────────────────────────────────────
 * import { createDraggableMixin } from '@/mixins/draggableMixin'
 *
 * export default {
 *   name: 'MyComponent',
 *   mixins: [
 *     createDraggableMixin({
 *       storageKey: 'my-component-position',
 *       dialogDimensions: { width: 400, height: 300 }
 *     })
 *   ]
 * }
 *
 * Step 2: Bind style and classes to the draggable element
 * ─────────────────────────────────────────────────────────────────────────────
 * <div class="my-panel" :style="dragStyle" :class="dragClasses">
 *   <!-- content -->
 * </div>
 *
 * Step 3: Add mousedown handler to drag handle
 * ─────────────────────────────────────────────────────────────────────────────
 * <header class="drag-handle" @mousedown="startDrag">
 *   Drag Me
 * </header>
 *
 * Step 4 (Optional): Add CSS for cursor feedback
 * ─────────────────────────────────────────────────────────────────────────────
 * .drag-handle { cursor: grab; user-select: none; }
 * .drag-handle:active { cursor: grabbing; }
 * .dragging .drag-handle { cursor: grabbing; }
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * FEATURES
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * - Dual mode: Reactive style binding OR direct DOM manipulation (for v-dialog)
 * - Fullscreen awareness: Auto-disables dragging in fullscreen mode
 * - Interactive element exclusion: Prevents drag on buttons/inputs in drag handle
 * - Touch support: Works on mobile devices
 * - Position persistence: Saves/loads position from localStorage with expiry
 * - Viewport constraints: Keeps element within visible bounds
 * - Memory safety: Proper event listener cleanup in beforeDestroy
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * MIXIN API - What you get when you use this mixin
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * COMPUTED PROPERTIES (bind these to your template):
 * ─────────────────────────────────────────────────────────────────────────────
 * - isDragging    {boolean}  Whether drag is in progress
 * - dragPosition  {x, y}     Current position (read-only)
 * - dragStyle     {object}   CSS style for :style binding (regular elements only)
 * - dragClasses   {object}   CSS classes for :class binding
 *
 * METHODS (call these from your component):
 * ─────────────────────────────────────────────────────────────────────────────
 * - startDrag(event)        Attach to @mousedown on drag handle
 * - applyDOMPosition()      Manual DOM update (required for v-dialog on open)
 * - resetDragPosition()     Reset to default position
 * - centerDragPosition()    Center element in viewport
 * - saveDragPosition()      Manually save to localStorage
 * - loadDragPosition()      Load from localStorage (returns true if loaded)
 * - constrainToViewport(pos) Returns position constrained to viewport
 * - getDragConfig()         Returns current configuration object
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * CONFIGURATION OPTIONS
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * | Option                | Type            | Default              | Description                                    |
 * |-----------------------|-----------------|----------------------|------------------------------------------------|
 * | dialogSelector        | string|null     | null                 | CSS selector for v-dialog (enables DOM mode)   |
 * | storageKey            | string|null     | null                 | localStorage key (auto-generates if null)      |
 * | persistPosition       | boolean         | true                 | Enable position persistence                    |
 * | defaultPosition       | {x, y}          | {x: 100, y: 100}     | Initial position                               |
 * | margin                | number          | 20                   | Viewport boundary margin (px)                  |
 * | dialogDimensions      | object|function | {width:400,height:300}| Element size for constraints                  |
 * | fullscreenProperty    | string|null     | 'isFullscreen'       | Property to check for fullscreen state         |
 * | dragHandleExclusions  | string[]        | ['.v-btn', ...]      | Selectors that won't trigger drag              |
 * | draggingClass         | string          | 'dragging'           | CSS class added during drag                    |
 * | zIndex                | number          | 1000                 | z-index (reactive mode only)                   |
 * | positionExpiry        | number          | 604800000 (7 days)   | Position expiry time (ms), 0 = never           |
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * USAGE EXAMPLES
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * EXAMPLE 1: Regular element (custom modal, panel, card)
 * ─────────────────────────────────────────────────────────────────────────────
 * // This is the simplest approach - just bind :style and :class
 *
 * <template>
 *   <v-sheet class="my-modal" :style="dragStyle" :class="dragClasses">
 *     <header @mousedown="startDrag">Drag Me</header>
 *     <div class="content">...</div>
 *   </v-sheet>
 * </template>
 *
 * <script>
 * import { createDraggableMixin } from '@/mixins/draggableMixin'
 * export default {
 *   mixins: [createDraggableMixin({
 *     storageKey: 'my-modal-position',
 *     dialogDimensions: { width: 500, height: 400 }
 *   })]
 * }
 * </script>
 *
 * EXAMPLE 2: Vuetify v-dialog (requires dialogSelector)
 * ─────────────────────────────────────────────────────────────────────────────
 * // v-dialog uses portals, so we need direct DOM manipulation
 *
 * <template>
 *   <v-dialog v-model="visible" :content-class="'my-dialog-class'">
 *     <v-card>
 *       <v-toolbar @mousedown="startDrag" :class="dragClasses">
 *         Title
 *       </v-toolbar>
 *     </v-card>
 *   </v-dialog>
 * </template>
 *
 * <script>
 * import { createDraggableMixin } from '@/mixins/draggableMixin'
 * export default {
 *   mixins: [createDraggableMixin({
 *     dialogSelector: '.my-dialog-class',  // REQUIRED for v-dialog
 *     fullscreenProperty: 'isFullscreen'
 *   })],
 *   watch: {
 *     visible(val) {
 *       if (val) this.applyDOMPosition()  // Apply position when dialog opens
 *     }
 *   }
 * }
 * </script>
 *
 * <style>
 * // MUST be global (not scoped) for v-dialog
 * .my-dialog-class { position: fixed !important; }
 * </style>
 *
 * EXAMPLE 3: Center on first open
 * ─────────────────────────────────────────────────────────────────────────────
 * watch: {
 *   showModal(isShown) {
 *     if (isShown && !this.loadDragPosition()) {
 *       this.centerDragPosition()
 *     }
 *   }
 * }
 *
 * EXAMPLE 4: Dynamic dimensions (responsive)
 * ─────────────────────────────────────────────────────────────────────────────
 * createDraggableMixin({
 *   dialogDimensions() {
 *     return {
 *       width: window.innerWidth * 0.6,
 *       height: window.innerHeight * 0.8
 *     }
 *   }
 * })
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * TROUBLESHOOTING
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * "Cannot set properties of undefined"
 *   → Vue 2 doesn't make _prefixed properties reactive. Fixed in v1.0.1.
 *
 * v-dialog position doesn't update
 *   → Set dialogSelector and call applyDOMPosition() when dialog opens.
 *
 * Buttons inside drag handle trigger drag
 *   → They're auto-excluded. Add custom exclusions if needed:
 *     dragHandleExclusions: ['.my-button']
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * IMPLEMENTATION REFERENCE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Real implementation: src/components/admin/popups/addAutomaticTagsPopup.vue
 * Full documentation: .claude/features/DRAGGABLE_MIXIN.md
 *
 */

import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

/**
 * Default configuration options
 * @type {Object}
 */
export const defaultDraggableConfig = {
  // CSS selector for v-dialog content element (enables direct DOM mode)
  // Set to null for regular elements using reactive style binding
  dialogSelector: null,

  // localStorage key for position persistence
  // Auto-generates from component name if null
  storageKey: null,

  // Enable/disable position persistence
  persistPosition: true,

  // Initial position if no saved position exists
  defaultPosition: { x: 100, y: 100 },

  // Viewport boundary margin in pixels
  margin: 20,

  // Dialog dimensions for constraint calculation
  // Can be: { width: number, height: number } OR function returning same
  dialogDimensions: { width: 400, height: 300 },

  // Property name on component to check for fullscreen state
  // Set to null to disable fullscreen checking
  fullscreenProperty: 'isFullscreen',

  // CSS selectors for elements that should NOT initiate drag
  dragHandleExclusions: [
    '.v-btn',
    'button',
    'input',
    'select',
    'textarea',
    '.v-icon--link',
    '[role="button"]'
  ],

  // CSS class added to element during dragging
  draggingClass: 'dragging',

  // z-index for positioned element (only used in reactive style mode)
  zIndex: 1000,

  // Position expiry time in ms (0 = never expires)
  // Default: 7 days
  positionExpiry: 7 * 24 * 60 * 60 * 1000
}

/**
 * Creates a draggable mixin with the specified configuration
 *
 * @param {Object} options - Configuration options
 * @param {string|null} options.dialogSelector - CSS selector for v-dialog (null for regular elements)
 * @param {string|null} options.storageKey - localStorage key (auto-generates if null)
 * @param {boolean} options.persistPosition - Enable position persistence
 * @param {Object} options.defaultPosition - Initial {x, y} position
 * @param {number} options.margin - Viewport boundary margin in pixels
 * @param {Object|Function} options.dialogDimensions - Dialog dimensions or getter function
 * @param {string|null} options.fullscreenProperty - Property name to check for fullscreen
 * @param {string[]} options.dragHandleExclusions - Selectors to ignore for drag start
 * @param {string} options.draggingClass - CSS class added during drag
 * @param {number} options.zIndex - z-index for positioned element
 * @param {number} options.positionExpiry - Position expiry time in ms
 * @returns {Object} Vue mixin object
 *
 * @example
 * // Regular element (uses reactive :style binding)
 * createDraggableMixin({
 *   dialogDimensions: { width: 300, height: 200 }
 * })
 *
 * @example
 * // Vuetify v-dialog (uses direct DOM manipulation)
 * createDraggableMixin({
 *   dialogSelector: '.my-dialog-class',
 *   fullscreenProperty: 'isFullscreen',
 *   dialogDimensions() {
 *     return { width: window.innerWidth * 0.6, height: window.innerHeight * 0.9 }
 *   }
 * })
 */
export function createDraggableMixin(options = {}) {
  // Merge user options with defaults
  const config = {
    ...defaultDraggableConfig,
    ...options,
    // Deep merge for nested objects
    defaultPosition: {
      ...defaultDraggableConfig.defaultPosition,
      ...(options.defaultPosition || {})
    },
    // Merge exclusions array (user can add more, not replace)
    dragHandleExclusions: options.dragHandleExclusions
      ? [...new Set([...defaultDraggableConfig.dragHandleExclusions, ...options.dragHandleExclusions])]
      : defaultDraggableConfig.dragHandleExclusions
  }

  return {
    data() {
      return {
        // Core drag state
        // Note: Using 'draggableMixin' prefix to avoid conflicts with component properties
        draggableMixinState: {
          position: { ...config.defaultPosition },
          isDragging: false,
          offset: { x: 0, y: 0 }
        },

        // Bound handlers for proper cleanup
        draggableMixinHandlers: {
          mousemove: null,
          mouseup: null,
          touchmove: null,
          touchend: null
        }
      }
    },

    computed: {
      /**
       * Whether dragging is currently active
       * @returns {boolean}
       */
      isDragging() {
        return this.draggableMixinState?.isDragging ?? false
      },

      /**
       * Current drag position
       * @returns {Object} {x, y} position
       */
      dragPosition() {
        return { ...(this.draggableMixinState?.position ?? config.defaultPosition) }
      },

      /**
       * Style object for reactive binding (non v-dialog elements)
       * Returns empty object if using dialogSelector mode or in fullscreen
       * @returns {Object} CSS style object
       */
      dragStyle() {
        // Skip if using direct DOM mode
        if (config.dialogSelector) {
          return {}
        }

        // Skip if in fullscreen mode
        if (config.fullscreenProperty && this[config.fullscreenProperty]) {
          return {}
        }

        // Safety check for initialization
        if (!this.draggableMixinState) {
          return {}
        }

        const pos = this.constrainToViewport(this.draggableMixinState.position)
        return {
          position: 'fixed',
          left: `${pos.x}px`,
          top: `${pos.y}px`,
          zIndex: config.zIndex,
          cursor: this.draggableMixinState.isDragging ? 'grabbing' : 'default'
        }
      },

      /**
       * CSS classes for drag state
       * @returns {Object} Class bindings object
       */
      dragClasses() {
        const classes = {
          [config.draggingClass]: this.draggableMixinState?.isDragging ?? false
        }

        // Add fullscreen class if property exists
        if (config.fullscreenProperty && this[config.fullscreenProperty] !== undefined) {
          classes['fullscreen'] = this[config.fullscreenProperty]
        }

        return classes
      }
    },

    mounted() {
      this.initDraggableMixin()
    },

    beforeDestroy() {
      // CRITICAL: Remove all event listeners to prevent memory leaks
      this.cleanupDragListeners()

      // Reset dragging state and body styles
      if (this.draggableMixinState?.isDragging) {
        this.draggableMixinState.isDragging = false
        document.body.style.userSelect = ''
        document.body.style.cursor = ''
      }
    },

    methods: {
      /**
       * Initialize draggable functionality
       * Creates bound handlers and loads saved position
       */
      initDraggableMixin() {
        // Bind handlers once for proper cleanup
        this.draggableMixinHandlers.mousemove = this.handleDragMove.bind(this)
        this.draggableMixinHandlers.mouseup = this.handleDragEnd.bind(this)
        this.draggableMixinHandlers.touchmove = this.handleDragMove.bind(this)
        this.draggableMixinHandlers.touchend = this.handleDragEnd.bind(this)

        // Load saved position
        if (config.persistPosition) {
          this.loadDragPosition()
        }
      },

      /**
       * Start dragging - attach to drag handle via @mousedown or @touchstart
       * @param {MouseEvent|TouchEvent} event
       */
      startDrag(event) {
        // Safety check
        if (!this.draggableMixinState) {
          return
        }

        // Check fullscreen mode
        if (config.fullscreenProperty && this[config.fullscreenProperty]) {
          return
        }

        // Only allow left mouse button for mouse events
        if (event.type === 'mousedown' && event.button !== 0) {
          return
        }

        // Check exclusions - prevent drag on interactive elements
        const target = event.target
        for (const selector of config.dragHandleExclusions) {
          if (target.closest(selector)) {
            return
          }
        }

        // Prevent text selection and default behaviors
        event.preventDefault()
        event.stopPropagation()

        // Get client coordinates (handle touch events)
        const clientX = event.touches?.[0]?.clientX ?? event.clientX
        const clientY = event.touches?.[0]?.clientY ?? event.clientY

        // Set drag state
        this.draggableMixinState.isDragging = true
        this.draggableMixinState.offset.x = clientX - this.draggableMixinState.position.x
        this.draggableMixinState.offset.y = clientY - this.draggableMixinState.position.y

        // Apply initial DOM position for v-dialog mode
        if (config.dialogSelector) {
          this.applyDOMPosition()
        }

        // Prevent text selection globally
        document.body.style.userSelect = 'none'
        document.body.style.cursor = 'grabbing'

        // Add document-level listeners for smooth dragging
        document.addEventListener('mousemove', this.draggableMixinHandlers.mousemove, { passive: false })
        document.addEventListener('mouseup', this.draggableMixinHandlers.mouseup)
        document.addEventListener('touchmove', this.draggableMixinHandlers.touchmove, { passive: false })
        document.addEventListener('touchend', this.draggableMixinHandlers.touchend)
      },

      /**
       * Handle drag movement
       * @param {MouseEvent|TouchEvent} event
       */
      handleDragMove(event) {
        if (!this.draggableMixinState?.isDragging) {
          return
        }

        // Check fullscreen (could change during drag)
        if (config.fullscreenProperty && this[config.fullscreenProperty]) {
          this.handleDragEnd()
          return
        }

        event.preventDefault()
        event.stopPropagation()

        // Get client coordinates
        const clientX = event.touches?.[0]?.clientX ?? event.clientX
        const clientY = event.touches?.[0]?.clientY ?? event.clientY

        // Calculate new position
        const newX = clientX - this.draggableMixinState.offset.x
        const newY = clientY - this.draggableMixinState.offset.y

        // Constrain to viewport and update position
        const constrained = this.constrainToViewport({ x: newX, y: newY })
        this.draggableMixinState.position = constrained

        // Direct DOM update for v-dialog mode
        if (config.dialogSelector) {
          this.applyDOMPosition()
        }
      },

      /**
       * End dragging
       * @param {MouseEvent|TouchEvent} [event]
       */
      handleDragEnd(event) {
        if (!this.draggableMixinState?.isDragging) {
          return
        }

        this.draggableMixinState.isDragging = false

        // Restore body styles
        document.body.style.userSelect = ''
        document.body.style.cursor = ''

        // Remove document listeners
        this.cleanupDragListeners()

        // Persist position
        if (config.persistPosition) {
          this.saveDragPosition()
        }

        if (event) {
          event.preventDefault()
          event.stopPropagation()
        }
      },

      /**
       * Remove all drag event listeners
       * CRITICAL for preventing memory leaks
       */
      cleanupDragListeners() {
        const handlers = this.draggableMixinHandlers
        if (!handlers) return

        if (handlers.mousemove) {
          document.removeEventListener('mousemove', handlers.mousemove)
        }
        if (handlers.mouseup) {
          document.removeEventListener('mouseup', handlers.mouseup)
        }
        if (handlers.touchmove) {
          document.removeEventListener('touchmove', handlers.touchmove)
        }
        if (handlers.touchend) {
          document.removeEventListener('touchend', handlers.touchend)
        }
      },

      /**
       * Apply position directly to DOM element
       * Required for v-dialog due to portal/teleport behavior
       * Call this method when dialog opens or fullscreen toggles
       */
      applyDOMPosition() {
        if (!config.dialogSelector) {
          return
        }

        this.$nextTick(() => {
          const el = document.querySelector(config.dialogSelector)
          if (!el || !this.draggableMixinState) {
            return
          }

          // Check fullscreen mode
          if (config.fullscreenProperty && this[config.fullscreenProperty]) {
            // Clear inline styles for fullscreen
            el.style.left = ''
            el.style.top = ''
            el.style.transform = ''
          } else {
            // Apply drag position
            el.style.left = `${this.draggableMixinState.position.x}px`
            el.style.top = `${this.draggableMixinState.position.y}px`
            el.style.transform = 'none'
          }
        })
      },

      /**
       * Constrain position to viewport boundaries
       * @param {Object} pos - {x, y} position
       * @returns {Object} Constrained {x, y} position
       */
      constrainToViewport(pos) {
        // Get dialog dimensions (supports function for dynamic sizing)
        const dims = typeof config.dialogDimensions === 'function'
          ? config.dialogDimensions.call(this)
          : config.dialogDimensions

        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const margin = config.margin

        // Calculate bounds
        const minX = margin
        const maxX = Math.max(margin, viewportWidth - dims.width - margin)
        const minY = margin
        const maxY = Math.max(margin, viewportHeight - dims.height - margin)

        return {
          x: Math.max(minX, Math.min(pos.x, maxX)),
          y: Math.max(minY, Math.min(pos.y, maxY))
        }
      },

      /**
       * Save position to localStorage
       */
      saveDragPosition() {
        try {
          if (!this.draggableMixinState) return

          const key = config.storageKey || `drag-position-${this.$options.name || 'component'}`
          localStorage.setItem(key, JSON.stringify({
            position: { ...this.draggableMixinState.position },
            timestamp: Date.now()
          }))
        } catch (e) {
          debugWarn('[DraggableMixin] Could not save position:', e)
        }
      },

      /**
       * Load position from localStorage
       * @returns {boolean} True if position was loaded successfully
       */
      loadDragPosition() {
        try {
          if (!this.draggableMixinState) return false

          const key = config.storageKey || `drag-position-${this.$options.name || 'component'}`
          const saved = localStorage.getItem(key)

          if (saved) {
            const { position, timestamp } = JSON.parse(saved)

            // Check expiry
            if (config.positionExpiry && timestamp && (Date.now() - timestamp > config.positionExpiry)) {
              localStorage.removeItem(key)
              return false
            }

            if (position && typeof position.x === 'number' && typeof position.y === 'number') {
              // Constrain to current viewport (in case screen size changed)
              this.draggableMixinState.position = this.constrainToViewport(position)
              return true
            }
          }
        } catch (e) {
          debugWarn('[DraggableMixin] Could not load position:', e)
        }
        return false
      },

      /**
       * Reset position to default
       */
      resetDragPosition() {
        if (!this.draggableMixinState) return

        this.draggableMixinState.position = { ...config.defaultPosition }

        if (config.dialogSelector) {
          this.applyDOMPosition()
        }

        if (config.persistPosition) {
          this.saveDragPosition()
        }
      },

      /**
       * Center the draggable element in viewport
       */
      centerDragPosition() {
        if (!this.draggableMixinState) return

        const dims = typeof config.dialogDimensions === 'function'
          ? config.dialogDimensions.call(this)
          : config.dialogDimensions

        this.draggableMixinState.position = {
          x: Math.max(config.margin, (window.innerWidth - dims.width) / 2),
          y: Math.max(config.margin, (window.innerHeight - dims.height) / 2)
        }

        if (config.dialogSelector) {
          this.applyDOMPosition()
        }

        if (config.persistPosition) {
          this.saveDragPosition()
        }
      },

      /**
       * Get the current configuration
       * Useful for debugging or accessing config values
       * @returns {Object} Current configuration
       */
      getDragConfig() {
        return { ...config }
      }
    }
  }
}
